/**
 * Unified task list with composable filters.
 * Fetches tasks server-side and manages status, type, assignee, and role filters.
 */
import { format } from 'date-fns'
import { AlertCircle, ChevronDown, ChevronUp, ClipboardPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import {
  type ClinicId,
  type DateFormat,
  formatCalendarDate,
  formatInstantDate,
  formatName,
  getTaskStatusLabel,
  isTaskSnoozed,
  LAB_TYPE_LABELS,
  PROCEDURE_TYPE_LABELS,
  STAFF_ROLES,
  type StaffRole,
  TASK_LIST_STATUSES,
  TASK_TYPE_LABELS,
  TASK_TYPES,
  type TaskListStatus,
  type TaskResponse,
  type TaskStatus,
  type TaskType,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { FileDownloadLink } from '@/frontend/components/file-download-link'
import { useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Pagination } from '@/frontend/components/pagination'
import { SearchInput } from '@/frontend/components/search-input'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { MultiSelectFilter } from '@/frontend/components/ui/multi-select-filter'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { formatProcedureFeeLine } from '@/frontend/features/patient/timeline/task-entry'
import { TaskActions } from '@/frontend/features/tasks/task-actions'
import { TaskCommentThread } from '@/frontend/features/tasks/task-comment-thread'
import { formatAssignee, getTaskStatusVariant } from '@/frontend/features/tasks/task-utils'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

import { NewTaskDialog } from './new-task-dialog'
import { useListMyTasks } from './use-list-my-tasks'
import { useListTasks } from './use-list-tasks'

const STATUS_ROW_COLORS: Record<TaskStatus, { row: string; detail: string }> = {
  open: { row: 'bg-info/20 hover:bg-info/40', detail: 'bg-info/20' },
  in_progress: { row: 'bg-pending/20 hover:bg-pending/40', detail: 'bg-pending/20' },
  completed: { row: 'bg-success/20 hover:bg-success/40', detail: 'bg-success/20' },
  cancelled: { row: '', detail: '' },
}

function TaskStatusBadge({ task }: { task: Pick<TaskResponse, 'type' | 'status'> }) {
  return <Badge variant={getTaskStatusVariant(task)}>{getTaskStatusLabel(task)}</Badge>
}

/** Sits beside the status badge while a task is parked, so the real status stays visible. */
function SnoozedBadge({ task, fmt }: { task: TaskResponse; fmt: DateFormat }) {
  if (!isTaskSnoozed(task) || !task.snoozedUntil) return null
  return (
    <Badge variant="neutral" title={formatInstantDate(task.snoozedUntil, fmt)}>
      Snoozed
    </Badge>
  )
}

function TaskTypeBadge({ type }: { type: TaskType }) {
  return <span>{TASK_TYPE_LABELS[type]}</span>
}

/**
 * Formats an **instant** as "8 Jun" when it falls in the current year, or the
 * full short date ("8 Jun 2024") when it's a different year. Used for the task
 * table's created column.
 */
function formatCompactInstant(value: string, fmt: DateFormat): string {
  const date = new Date(value)
  if (date.getFullYear() === new Date().getFullYear()) {
    return date.toLocaleDateString(fmt.locale, {
      day: 'numeric',
      month: 'short',
      timeZone: fmt.timeZone,
    })
  }
  return formatInstantDate(value, fmt)
}

/**
 * The same compact form for a **calendar date**. `task.dueDate` is a `z.iso.date()`
 * field naming a day, so it is read as written — parsing it as an instant renders
 * the previous day for any viewer west of Greenwich.
 */
function formatCompactCalendarDate(value: string, fmt: DateFormat): string {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  if (year === new Date().getFullYear()) {
    return new Date(year, month - 1, day).toLocaleDateString(fmt.locale, {
      day: 'numeric',
      month: 'short',
    })
  }
  return formatCalendarDate(value, fmt)
}

/** Extracts the explicit due date from task data if present. */
function getDueDate(task: TaskResponse): string | null {
  if ('dueDate' in task.data && task.data.dueDate) {
    return task.data.dueDate
  }
  return null
}

/** Renders the type-specific detail fields for an expanded task row. */
function TaskDetailData({ task }: { task: TaskResponse }) {
  const data = task.data
  // A task isn't recorded at a clinic — the assignee's clinic is where the work
  // lands, not where the task happened — so its dates follow the viewer.
  const fmt = useViewerFormat()

  return (
    <>
      {isTaskSnoozed(task) && task.snoozedUntil && (
        <DetailField label="Snoozed until" value={formatInstantDate(task.snoozedUntil, fmt)} />
      )}
      {'urgent' in data && data.urgent && <DetailField label="Urgency" value="Urgent" />}
      {'dueDate' in data && data.dueDate && (
        // `dueDate` names a day, not a moment.
        <DetailField label="Due date" value={formatCalendarDate(data.dueDate, fmt)} />
      )}
      {data.type === 'labs' && data.labType && (
        <DetailField label="Lab type" value={LAB_TYPE_LABELS[data.labType]} />
      )}
      {data.type === 'wet_script_request' && data.treatment && (
        <DetailField
          label="Prescription"
          value={`${data.treatment.productName} × ${data.treatment.quantity} — ${data.treatment.instructions}`}
        />
      )}
      {data.type === 'wet_script_request' && data.treatment && (
        <DetailField
          label="Prescription"
          value={[
            `${data.treatment.productName} × ${data.treatment.quantity}`,
            data.treatment.instructions,
          ]
            .filter(Boolean)
            .join(' — ')}
        />
      )}
      {data.type === 'procedure_request' && (
        <>
          <DetailField label="Procedure" value={PROCEDURE_TYPE_LABELS[data.procedure]} />
          <DetailField label="Fee" value={formatProcedureFeeLine(data, task.status, fmt)} />
          <DetailField label="Note" value={data.note} />
        </>
      )}
    </>
  )
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <span className="text-muted-foreground">{label}:</span>
      <span>{value}</span>
    </div>
  )
}

/** A single task table row that expands to show full details on click. */
function TaskRow({ task }: { task: TaskResponse }) {
  const [expanded, setExpanded] = useState(false)
  // See `TaskDetailData` — a task has no clinic of its own, so it renders in the
  // viewer's zone and conventions.
  const fmt = useViewerFormat()
  const dueDate = getDueDate(task)
  const isUrgent = 'urgent' in task.data && task.data.urgent
  const assignee = formatAssignee(task)
  const colors = isTaskSnoozed(task) ? { row: '', detail: '' } : STATUS_ROW_COLORS[task.status]

  return (
    <>
      <TableRow
        className={cn('cursor-pointer', colors.row)}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <TableCell className="w-6 pl-4 pr-1">
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0 text-muted-foreground transition-transform',
              !expanded && '-rotate-90'
            )}
          />
        </TableCell>
        <TableCell className="text-center whitespace-normal">
          <TaskTypeBadge type={task.type} />
        </TableCell>
        <TableCell className="max-w-48 truncate" title={task.title}>
          {task.title}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {task.patient ? (
            <Link
              to={generatePath(routes.patient, { patientId: task.patient.id })}
              className="text-primary underline underline-offset-2"
            >
              {task.patient.name}
            </Link>
          ) : null}
        </TableCell>
        <TableCell className="max-w-26 truncate" title={assignee}>
          {assignee}
        </TableCell>
        <TableCell className="text-center">
          <div className="flex flex-wrap justify-center gap-1">
            <TaskStatusBadge task={task} />
            <SnoozedBadge task={task} fmt={fmt} />
          </div>
        </TableCell>
        <TableCell className="text-center">
          {isUrgent && <AlertCircle className="size-3.5 text-destructive-fg mx-auto" />}
        </TableCell>
        <TableCell className="tabular-nums text-center">
          {dueDate ? formatCompactCalendarDate(dueDate, fmt) : '—'}
        </TableCell>
        <TableCell
          className="tabular-nums text-center"
          title={formatInstantDate(task.createdAt, fmt)}
        >
          {formatCompactInstant(task.createdAt, fmt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className={cn(colors.detail)}>
          <TableCell colSpan={9} className="py-3 pl-10 pr-4 space-y-1">
            <p className="font-medium whitespace-pre-wrap break-words">{task.title}</p>
            <TaskDetailData task={task} />
            {task.creator && formatName(task.creator) && (
              <DetailField label="Created by" value={formatName(task.creator)!} />
            )}
            {task.files.length > 0 && (
              <div className="flex gap-1.5">
                <span className="text-muted-foreground">Files:</span>
                <div className="flex flex-wrap gap-2">
                  {task.files.map((file) => (
                    <FileDownloadLink key={file.id} fileId={file.id} fileName={file.fileName} />
                  ))}
                </div>
              </div>
            )}
            <TaskCommentThread task={task} />
            <div className="pt-2">
              <TaskActions task={task} variant="buttons" />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

type SortBy = 'urgency' | 'due_date' | 'created_at'
type SortOrder = 'asc' | 'desc'

/**
 * Natural default direction for each sort column, applied the first time a
 * column becomes active. Re-clicking the active column flips the direction.
 */
const DEFAULT_SORT_ORDER: Record<SortBy, SortOrder> = {
  urgency: 'desc',
  due_date: 'asc',
  created_at: 'desc',
}

/**
 * A clickable column header that sorts by `column`. Shows a directional arrow
 * when it's the active sort column so the user can see the current order.
 */
function SortableHead({
  label,
  column,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string
  column: SortBy
  sortBy: SortBy
  sortOrder: SortOrder
  onSort: (column: SortBy) => void
}) {
  const active = sortBy === column
  return (
    <TableHead
      className={cn(
        'text-center cursor-pointer hover:text-primary transition-colors',
        active && 'text-primary'
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center justify-center gap-0.5">
        {label}
        {active &&
          (sortOrder === 'asc' ? (
            <ChevronUp className="size-3.5" aria-label="ascending" />
          ) : (
            <ChevronDown className="size-3.5" aria-label="descending" />
          ))}
      </span>
    </TableHead>
  )
}

function TasksTable({
  tasks,
  sortBy,
  sortOrder,
  onSort,
}: {
  tasks: TaskResponse[]
  sortBy: SortBy
  sortOrder: SortOrder
  onSort: (column: SortBy) => void
}) {
  return (
    <Table size="sm" variant="borderless">
      <TableHeader>
        <TableRow>
          <TableHead className="w-6 pl-4 pr-1" />
          <TableHead className="text-center w-12">Type</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Patient</TableHead>
          <TableHead>Assignee</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <SortableHead
            label="Urgent"
            column="urgency"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
          <SortableHead
            label="Due"
            column="due_date"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
          <SortableHead
            label="Created"
            column="created_at"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} />
        ))}
      </TableBody>
    </Table>
  )
}

const TYPE_ITEMS = [{ value: null, label: 'All types' }, ...TASK_TYPES]

/** Composable filter controls for the unified task list. */
function TaskFilters({
  statusFilter,
  typeFilter,
  assigneeStaffId,
  roleFilter,
  clinicFilter,
  onStatusChange,
  onTypeChange,
  onAssigneeChange,
  onRoleChange,
  onClinicChange,
  currentStaffId,
  userScoped,
  onUserScopedChange,
  hideOldTasks,
  onHideOldTasksChange,
}: {
  statusFilter: TaskListStatus[]
  typeFilter: string | undefined
  assigneeStaffId: string | undefined
  roleFilter: StaffRole | undefined
  clinicFilter: ClinicId | undefined
  onStatusChange: (v: TaskListStatus[]) => void
  onTypeChange: (v: string | undefined) => void
  onAssigneeChange: (staffId: string | undefined) => void
  onRoleChange: (role: StaffRole | undefined) => void
  onClinicChange: (clinicId: ClinicId | undefined) => void
  /** The authenticated user's staff ID — used to gate the My Tasks checkbox. */
  currentStaffId: string | undefined
  /** Whether the list is scoped to the current user (personal + team). */
  userScoped: boolean
  onUserScopedChange: (checked: boolean) => void
  /** Whether tasks older than 30 days are hidden. */
  hideOldTasks: boolean
  onHideOldTasksChange: (checked: boolean) => void
}) {
  // Progressive disclosure: assignee + role dropdowns are hidden under My
  // Tasks since the scope subsumes them. The clinic picker stays visible
  // either way — staff move between clinics often and need to be able to
  // refine in the moment.
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground w-20">Whose tasks</span>
        {currentStaffId && (
          <label
            htmlFor="my-tasks-checkbox"
            className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
          >
            <Checkbox
              id="my-tasks-checkbox"
              checked={userScoped}
              onCheckedChange={(checked) => onUserScopedChange(!!checked)}
            />
            My Tasks
          </label>
        )}
        {!userScoped && (
          <>
            <StaffSearch
              value={assigneeStaffId ?? null}
              onChange={(member) => onAssigneeChange(member?.id)}
              allowClear
              placeholder="Anyone"
              className="h-8 w-44 text-sm"
            />
            <Select
              items={STAFF_ROLES}
              value={roleFilter ?? null}
              onValueChange={(v) => onRoleChange(v ?? undefined)}
            >
              <SelectTrigger
                className="h-8 w-44 text-sm"
                onClear={roleFilter ? () => onRoleChange(undefined) : undefined}
                clearLabel="Clear role filter"
              >
                <SelectValue placeholder="Any role" />
              </SelectTrigger>
              <SelectContent>
                {STAFF_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <span className="text-sm text-muted-foreground">at</span>
        <ClinicSearchWithDefault
          includeArchived
          value={clinicFilter ?? null}
          onChange={(id) => onClinicChange(id ?? undefined)}
          allowClear
          className="h-8 w-44 text-sm"
          placeholder="Any clinic"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground w-20">Which tasks</span>
        <MultiSelectFilter
          filterKey="tasks"
          options={TASK_LIST_STATUSES}
          selected={statusFilter}
          onChange={(values) => onStatusChange(values as TaskListStatus[])}
          placeholder="All statuses"
        />
        <Select
          items={TYPE_ITEMS}
          value={typeFilter ?? null}
          onValueChange={(v) => onTypeChange(v ?? undefined)}
        >
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            {TYPE_ITEMS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label
          htmlFor="exclude-stale-checkbox"
          className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
        >
          <Checkbox
            id="exclude-stale-checkbox"
            checked={hideOldTasks}
            onCheckedChange={(checked) => onHideOldTasksChange(!!checked)}
          />
          Hide old tasks
        </label>
      </div>
    </div>
  )
}

const PAGE_SIZE = 25

/**
 * Single unified task list with a "My Tasks" shortcut that scopes the list
 * to the current user's personal + team assignments (via `GET /tasks/mine`).
 * When unticked, the individual filter dropdowns drive the generic list query.
 * Both modes paginate server-side.
 */
export function UnifiedTaskList() {
  const { data: currentStaff } = useGetAuthenticatedStaff()

  const [statusFilter, setStatusFilter] = useState<TaskListStatus[]>(['open', 'in_progress'])
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined)
  const [assigneeStaffId, setAssigneeStaffId] = useState<string | undefined>(undefined)
  const [roleFilter, setRoleFilter] = useState<StaffRole | undefined>(undefined)
  const [clinicFilter, setClinicFilter] = useState<ClinicId | undefined>(undefined)
  const [userScoped, setUserScoped] = useState(true)
  const [hideOldTasks, setHideOldTasks] = useState(true)
  const [sortBy, setSortBy] = useState<SortBy>('urgency')
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER.urgency)
  const [page, setPage] = useState(1)
  // Prevents firing the tasks query before defaults have been initialised from currentStaff.
  const [filtersReady, setFiltersReady] = useState(false)
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false)
  const [search, setSearch] = useState('')
  const q = useDebounce(search, 300).trim() || undefined

  useEffect(() => {
    if (currentStaff && !filtersReady) {
      // Seed the dropdowns so that when the user unticks "My Tasks" they land
      // on a sensible custom-filter starting point (their own assignee/role)
      // rather than empty fields.
      if (currentStaff.role) setRoleFilter(currentStaff.role)
      setAssigneeStaffId(currentStaff.id)
      setFiltersReady(true)
    }
  }, [currentStaff, filtersReady])

  function handleUserScopedChange(checked: boolean) {
    setUserScoped(checked)
    setPage(1)
  }

  // Reset to the first page whenever any filter changes
  function handleStatusChange(value: TaskListStatus[]) {
    setStatusFilter(value)
    setPage(1)
  }
  function handleTypeChange(value: string | undefined) {
    setTypeFilter(value)
    setPage(1)
  }
  function handleAssigneeChange(value: string | undefined) {
    setAssigneeStaffId(value)
    setPage(1)
  }
  function handleRoleChange(role: StaffRole | undefined) {
    setRoleFilter(role)
    setPage(1)
  }
  function handleClinicChange(clinicId: ClinicId | undefined) {
    setClinicFilter(clinicId)
    setPage(1)
  }
  function handleSearchChange(value: string) {
    setSearch(value)
    setPage(1)
  }
  // Clicking the active sort column flips the direction; clicking a different
  // column switches to it using that column's natural default direction.
  function handleSort(column: SortBy) {
    if (column === sortBy) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder(DEFAULT_SORT_ORDER[column])
    }
    setPage(1)
  }

  const createdAfter = hideOldTasks
    ? format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
    : undefined
  const status = statusFilter.length > 0 ? statusFilter : undefined
  const type = typeFilter as TaskType | undefined

  // The clinic picker drives the team-clinic scoping literally: a chosen
  // clinic narrows it, an empty picker (null) means "all clinics". We pass
  // null rather than undefined so the hook doesn't fall back to the user's
  // default clinic preference — on the page the picker itself already starts
  // populated with that default via ClinicSearchWithDefault.
  const myTasksQuery = useListMyTasks(
    {
      q,
      status,
      type,
      clinicId: clinicFilter ?? null,
      createdAfter,
      sortBy,
      sortOrder,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { enabled: filtersReady && userScoped, refreshInterval: 20000 }
  )

  const customQuery = useListTasks(
    {
      q,
      status,
      type,
      assigneeStaffId,
      assigneeRole: roleFilter,
      assigneeClinicId: clinicFilter,
      createdAfter,
      sortBy,
      sortOrder,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { enabled: filtersReady && !userScoped, refreshInterval: 20000 }
  )

  const activeQuery = userScoped ? myTasksQuery : customQuery
  const isLoading = activeQuery.isLoading
  const tasks = activeQuery.data?.tasks ?? []
  const totalPages = activeQuery.data ? Math.ceil(activeQuery.data.totalCount / PAGE_SIZE) : 0

  return (
    <div>
      <NewTaskDialog open={newTaskDialogOpen} onOpenChange={setNewTaskDialogOpen} />
      <div className="flex items-start justify-between mb-3">
        <TaskFilters
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          assigneeStaffId={assigneeStaffId}
          roleFilter={roleFilter}
          clinicFilter={clinicFilter}
          onStatusChange={handleStatusChange}
          onTypeChange={handleTypeChange}
          onAssigneeChange={handleAssigneeChange}
          onRoleChange={handleRoleChange}
          onClinicChange={handleClinicChange}
          currentStaffId={currentStaff?.id}
          userScoped={userScoped}
          onUserScopedChange={handleUserScopedChange}
          hideOldTasks={hideOldTasks}
          onHideOldTasksChange={(checked) => {
            setHideOldTasks(checked)
            setPage(1)
          }}
        />
        <Button size="lg" onClick={() => setNewTaskDialogOpen(true)}>
          <ClipboardPlus />
          New Task
        </Button>
      </div>
      <div className="mb-3 flex">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by title or patient name..."
        />
      </div>
      {!filtersReady || isLoading ? (
        <p className="text-muted-foreground py-4">Loading...</p>
      ) : tasks.length === 0 ? (
        <p className="text-muted-foreground py-4">
          {q ? 'No tasks match your search' : 'No tasks to show here'}
        </p>
      ) : (
        <>
          <TasksTable tasks={tasks} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
